import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, Repeat, Plus, Send, Pause, Play, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const SCHEDULES = [
  { key: 'monthly',    label: 'Monthly' },
  { key: 'quarterly',  label: 'Quarterly' },
  { key: 'semiannual', label: 'Semi-annual' },
  { key: 'annual',     label: 'Annual' },
  { key: 'weekly',     label: 'Weekly' },
];

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/**
 * Pledges tab — recurring gift schedules & multi-year pledges. Supports
 * add/edit/pause/resume, post-installment (creates a real gift), and overdue
 * highlighting.
 */
export function PledgesTab({ api, donorId }) {
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = donorId ? `/donor-pledges?donor_id=${donorId}` : '/donor-pledges';
      const { data } = await api.get(url);
      setPledges(data);
    } catch { toast.error('Failed to load pledges'); }
    setLoading(false);
  }, [api, donorId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (p) => {
    const newStatus = p.status === 'active' ? 'paused' : 'active';
    try { await api.put(`/donor-pledges/${p.id}`, { status: newStatus }); toast.success(newStatus); load(); }
    catch { toast.error('Failed'); }
  };
  const remove = async (p) => {
    if (!window.confirm('Delete this pledge?')) return;
    try { await api.delete(`/donor-pledges/${p.id}`); load(); }
    catch { toast.error('Failed'); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const overdue = pledges.filter(p => p.status === 'active' && p.next_expected && p.next_expected < today);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#6B3FA0]" /></div>;

  return (
    <div className="space-y-3" data-testid="pledges-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Repeat className="h-5 w-5 text-[#6B3FA0]" />
          <span className="font-semibold">Pledges & recurring gifts</span>
          {overdue.length > 0 && (
            <Badge className="bg-red-100 text-red-800 border-0"><AlertCircle className="h-3 w-3 mr-0.5" /> {overdue.length} overdue</Badge>
          )}
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="pledge-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New pledge
        </Button>
      </div>

      {pledges.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">No pledges yet.</CardContent></Card>
      ) : (
        <div className="grid gap-1.5">
          {pledges.map(p => {
            const isOverdue = p.status === 'active' && p.next_expected && p.next_expected < today;
            const progress = p.total_expected ? Math.min(100, (p.total_received / p.total_expected) * 100) : 0;
            return (
              <Card key={p.id} data-testid={`pledge-row-${p.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                      {!donorId && <span className="font-semibold">{p.donor_name}</span>}
                      <Badge variant="outline" className="text-[10px] capitalize">{p.kind}</Badge>
                      <Badge variant="outline" className="text-[10px] capitalize">{p.schedule}</Badge>
                      <span className="text-sm">{fmt$(p.amount)} / installment</span>
                      <Badge className={`text-[10px] border-0 capitalize ${
                        p.status === 'active' ? 'bg-emerald-100 text-emerald-800'
                        : p.status === 'paused' ? 'bg-amber-100 text-amber-800'
                        : p.status === 'completed' ? 'bg-blue-100 text-blue-800'
                        : 'bg-red-100 text-red-800'}`}>{p.status}</Badge>
                      {isOverdue && <Badge className="bg-red-100 text-red-800 border-0 text-[10px]">overdue</Badge>}
                    </div>
                    <div className="flex items-center gap-1">
                      {p.status === 'active' && (
                        <Button size="sm" onClick={() => setPostOpen(p)} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`pledge-post-${p.id}`}>
                          <Send className="h-3 w-3 mr-1" /> Post installment
                        </Button>
                      )}
                      {p.status !== 'completed' && (
                        <Button size="sm" variant="outline" onClick={() => toggle(p)} data-testid={`pledge-toggle-${p.id}`}>
                          {p.status === 'active' ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-red-500"
                              data-testid={`pledge-delete-${p.id}`}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Next: <span className="font-medium">{p.next_expected || '—'}</span>
                    {p.total_expected > 0 && <> · {fmt$(p.total_received)} / {fmt$(p.total_expected)} · {p.installments_received} installments</>}
                    {p.campaign && <> · {p.campaign}</>}
                  </div>
                  {p.total_expected > 0 && (
                    <div className="h-1 bg-gray-200 rounded mt-1 overflow-hidden">
                      <div className="h-full bg-[#6B3FA0]" style={{ width: `${progress}%` }} />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AddPledgeDialog open={addOpen} onClose={() => setAddOpen(false)} api={api}
                       donorId={donorId} onDone={load} />
      <PostInstallmentDialog open={!!postOpen} onClose={() => setPostOpen(null)} api={api}
                             pledge={postOpen} onDone={load} />
    </div>
  );
}

function AddPledgeDialog({ open, onClose, api, donorId, onDone }) {
  const [form, setForm] = useState({ donor_id: donorId || '', kind: 'recurring', schedule: 'monthly',
                                      amount: '', start_date: new Date().toISOString().slice(0, 10),
                                      total_expected: '', campaign: '', notes: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(f => ({ ...f, donor_id: donorId || f.donor_id })); }, [open, donorId]);
  if (!open) return null;

  const save = async () => {
    if (!form.donor_id) { toast.error('Donor required'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Amount required'); return; }
    setBusy(true);
    try {
      await api.post('/donor-pledges', { ...form, amount: Number(form.amount), total_expected: Number(form.total_expected || 0) });
      toast.success('Pledge created'); onDone(); onClose();
    } catch { toast.error('Save failed'); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="add-pledge-dialog">
        <DialogHeader><DialogTitle>New pledge / recurring gift</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Type</Label>
              <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}
                      className="border rounded px-2 py-1.5 w-full text-sm">
                <option value="recurring">Recurring (open-ended)</option>
                <option value="pledge">Pledge (fixed total)</option>
              </select>
            </div>
            <div>
              <Label>Schedule</Label>
              <select value={form.schedule} onChange={e => setForm(f => ({ ...f, schedule: e.target.value }))}
                      className="border rounded px-2 py-1.5 w-full text-sm">
                {SCHEDULES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Amount / installment</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                     data-testid="pledge-amount" /></div>
            <div><Label>Start date</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} /></div>
          </div>
          {form.kind === 'pledge' && (
            <div><Label>Total pledged</Label>
              <Input type="number" step="0.01" value={form.total_expected}
                     onChange={e => setForm(f => ({ ...f, total_expected: e.target.value }))} /></div>
          )}
          <div><Label>Campaign</Label>
            <Input value={form.campaign} onChange={e => setForm(f => ({ ...f, campaign: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="pledge-save-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PostInstallmentDialog({ open, onClose, api, pledge, onDone }) {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), amount: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (pledge) setForm({ date: new Date().toISOString().slice(0, 10), amount: pledge.amount }); }, [pledge]);
  if (!open || !pledge) return null;

  const post = async () => {
    setBusy(true);
    try {
      await api.post(`/donor-pledges/${pledge.id}/post-installment`,
                     { date: form.date, amount: Number(form.amount || pledge.amount) });
      toast.success('Installment posted');
      onDone(); onClose();
    } catch { toast.error('Failed'); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="post-installment-dialog">
        <DialogHeader><DialogTitle>Post installment — {pledge.donor_name}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><Label>Gift date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
          <div><Label>Amount</Label><Input type="number" step="0.01" value={form.amount}
                                            onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
          <div className="text-xs text-gray-500">Next expected after posting: {pledge.next_expected}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={post} disabled={busy} data-testid="post-installment-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post gift'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
